import { VideoPolicy } from "../constants/video-policies";

/**
 * Mirror of the server's per-file probe result (POST /services/probe-video).
 * Fields are `null` when `success === false`.
 */
export interface VideoProbe {
    success: boolean;
    name: string;
    container: string | null;
    codec: string | null;
    width: number | null;
    height: number | null;
    fps: number | null;
    duration: number | null;
    sizeBytes: number | null;
    hasAudio: boolean | null;
    audioCodec: string | null;
    probeError: string | null;
    thumbnail: string | null;
}

export interface ProbeValidationResult {
    valid: boolean;
    /** User-facing error message; only present when `valid === false`. */
    error?: string;
}

/**
 * Apply a video policy to a probe result. Returns the first violation found
 * (first-failure-wins keeps the user-facing error message focused).
 *
 * Error wording mirrors the previous client-side validator in
 * `CaptionedVideoMediaStep` so the user experience does not change.
 */
export const validateVideoProbe = (
    probe: VideoProbe,
    policy: VideoPolicy
): ProbeValidationResult => {
    if (!probe.success) {
        return { valid: false, error: probe.probeError ?? "Something went wrong." };
    }

    if (
        probe.container &&
        !policy.allowedContainers.includes(probe.container.toLowerCase())
    ) {
        return {
            valid: false,
            error: `"${probe.name}" is not a supported format. Please upload your video as MP4, MOV, or WebM.`,
        };
    }

    if (probe.duration !== null && probe.duration > policy.maxDurationS) {
        return {
            valid: false,
            error: "Your video exceeds the 2-minute limit. Please upload a video shorter than 2 minutes.",
        };
    }

    if (probe.duration !== null && probe.duration < policy.minDurationS) {
        return {
            valid: false,
            error: "Your video is too short. Please upload a video at least 4 seconds long.",
        };
    }

    if (probe.sizeBytes !== null && probe.sizeBytes > policy.maxBytes) {
        return {
            valid: false,
            error: "Your video exceeds the 600 MB size limit. Please upload a video within the allowed size.",
        };
    }

    if (
        probe.width !== null &&
        probe.height !== null &&
        Math.min(probe.width, probe.height) < policy.minShortSidePx
    ) {
        return {
            valid: false,
            error: "Your video resolution is too low. Please upload a HD video.",
        };
    }

    if (
        probe.width !== null &&
        probe.height !== null &&
        Math.max(probe.width, probe.height) > policy.maxDimensionPx
    ) {
        return {
            valid: false,
            error: "Your video resolution exceeds the supported limit. Please upload a Full HD or lower resolution video.",
        };
    }

    if (probe.fps !== null && probe.fps > policy.maxFps) {
        return {
            valid: false,
            error: "Your video has a frame rate that is too high. Please upload a video with a lower frame rate.",
        };
    }

    return { valid: true };
};
